import React, { useState, useRef, useEffect } from 'react';

/**
 * MultiSelect Component
 * 
 * A dropdown that allows selecting multiple options
 * 
 * @param {array} options - Array of options (objects with id and name, or strings)
 * @param {array} value - Array of selected values (IDs)
 * @param {function} onChange - Callback when selection changes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether the select is disabled
 * @param {string} id - HTML id attribute
 */
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options...',
  disabled = false,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (optionId) => {
    if (disabled) return;

    const newValue = value.includes(optionId)
      ? value.filter(id => id !== optionId)
      : [...value, optionId];
    
    onChange(newValue);
  };

  const getOptionLabel = (option) => {
    return typeof option === 'object' ? option.name : option;
  };

  const getOptionId = (option) => {
    return typeof option === 'object' ? option.id : option;
  };

  const getSelectedLabels = () => {
    if (!value || value.length === 0) return placeholder;
    
    return options
      .filter(opt => value.includes(getOptionId(opt)))
      .map(opt => getOptionLabel(opt))
      .join(', ');
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div
        id={id}
        onClick={handleToggle}
        className={`
          w-full px-3 py-2 border border-gray-300 rounded-lg 
          focus:ring-2 focus:ring-primary-400 focus:border-primary-400 
          cursor-pointer flex items-center justify-between
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
          ${isOpen ? 'ring-2 ring-primary-400 border-primary-400' : ''}
        `}
      >
        <span className={`truncate ${!value || value.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>
          {getSelectedLabels()}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-gray-500 text-sm">No options available</div>
          ) : (
            options.map((option, index) => {
              const optionId = getOptionId(option);
              const optionLabel = getOptionLabel(option);
              const isSelected = value.includes(optionId);

              return (
                <div
                  key={index}
                  onClick={() => handleOptionClick(optionId)}
                  className={`
                    px-3 py-2 cursor-pointer flex items-center space-x-2
                    hover:bg-primary-50 transition-colors
                    ${isSelected ? 'bg-primary-100' : ''}
                  `}
                >
                  <div className={`
                    w-4 h-4 border rounded flex items-center justify-center
                    ${isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}
                  `}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-gray-900">{optionLabel}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;

